import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import { useInjectSaga } from '../../utils/injectSaga';
import saga from './saga';
import { useInjectReducer } from '../../utils/injectReducer';
import reducer from './reducer';
import { getPartnersAction, resetStateAction, getListPartnerFileAction } from './actions';
import {
	getPartnersSelector,
	getIsPartnersLoadingSelector,
	getPaginationSelector,
	getIsDownloadLoadingSelector,
	getCompanyTypeIdSelector,
} from './selectors';
import { getIsAvailablePartnersButtonsSelector } from '../profile/selectors';
import { removeModalQuery } from '../../utils/removeModalQuery';
import { TABLE_COLUMNS } from './constants';
import { normalizeDataGridModel } from '../../utils/normalizeDataGridModel';
import { DataGridComponent } from '../../components/data-grid';

const PartnersContainerWrapper = ({
	onGetPartners,
	partners,
	isPartnersLoading,
	companyTypeId,
	onResetState,
	pagination,
	isAvailable,
	onGetListPartnerFile,
	isDownloadLoading,
}) => {
	useInjectSaga({ key: 'partnersSaga', saga });
	useInjectReducer({ key: 'partnersReducer', reducer });

	const { search, pathname } = useLocation();

	const query = new URLSearchParams(search);

	const history = useHistory();

	useEffect(() => {
		onGetPartners({ searchQuery: removeModalQuery(query.toString()) });

		return () => onResetState();
	}, []);

	const handleAddButtonClick = () => {
		query.set('modal', 'partner');
		query.set('mode', 'add');

		history.push(`${pathname}?${query}`);
	};

	const handlePageChange = (_, { activePage }) => {
		query.set('page', activePage);
		history.push(`${pathname}?${query}`);
		onGetPartners({ searchQuery: removeModalQuery(query.toString()) });
	};

	const handleSelectChange = (_, { value }) => {
		query.set('page', 1);
		if (value === 'all') {
			query.delete('per_page');
			history.push(`${pathname}?${query}`);
		} else {
			query.set('per_page', value);
			history.push(`${pathname}?${query}`);
		}
		onGetPartners({ searchQuery: removeModalQuery(query.toString()) });
	};

	const handleDownloadButtonClick = () => {
		onGetListPartnerFile({ exportUrl: '/api/partners/export' });
	};

	const handleSortChange = useCallback(sortModel => {
		const jsonSorts = JSON.stringify(sortModel);

		query.set('sorts', jsonSorts);

		history.push(`${pathname}?${query}`);
		onGetPartners({ searchQuery: removeModalQuery(query.toString()) });
	});

	const handleFilterChange = useCallback((filterModel, action) => {
		if (action.reason) {
			const jsonFilters = JSON.stringify(normalizeDataGridModel(filterModel, TABLE_COLUMNS(companyTypeId)));

			query.delete('page');
			query.set('filters', jsonFilters);

			history.push(`${pathname}?${query}`);
			onGetPartners({ searchQuery: removeModalQuery(query.toString()) });
		}
	});

	return (
		<DataGridComponent
			headerText="Контрагенты"
			onAddButtonClick={isAvailable.create ? handleAddButtonClick : null}
			onDownloadButtonClick={isAvailable.export ? handleDownloadButtonClick : null}
			isDownloadButtonLoading={isDownloadLoading}
			columns={TABLE_COLUMNS(companyTypeId)}
			rows={partners}
			isTableDataLoading={isPartnersLoading}
			pagination={
				!pagination
					? null
					: { ...pagination, onPageChange: handlePageChange, onSelectChange: handleSelectChange }
			}
			handleSortChange={handleSortChange}
			handleFilterChange={handleFilterChange}
		/>
	);
};

const mapStateToProps = createStructuredSelector({
	isPartnersLoading: getIsPartnersLoadingSelector(),
	partners: getPartnersSelector(),
	pagination: getPaginationSelector(),
	isAvailable: getIsAvailablePartnersButtonsSelector(),
	isDownloadLoading: getIsDownloadLoadingSelector(),
	companyTypeId: getCompanyTypeIdSelector(),
});

const mapDispatchToProps = {
	onGetPartners: getPartnersAction,
	onResetState: resetStateAction,
	onGetListPartnerFile: getListPartnerFileAction,
};

PartnersContainerWrapper.propTypes = {
	onGetListPartnerFile: PropTypes.func.isRequired,
	onGetPartners: PropTypes.func.isRequired,
	isPartnersLoading: PropTypes.bool.isRequired,
	isDownloadLoading: PropTypes.bool.isRequired,
	companyTypeId: PropTypes.number,
	partners: PropTypes.array.isRequired,
	onResetState: PropTypes.func.isRequired,
	pagination: PropTypes.shape({
		currentPage: PropTypes.number,
		totalPages: PropTypes.number.isRequired,
		total: PropTypes.number,
		from: PropTypes.number,
		to: PropTypes.number,
		isNextItem: PropTypes.bool,
		isPrevItem: PropTypes.bool,
		isFirstItem: PropTypes.bool,
		isLastItem: PropTypes.bool,
	}),
	isAvailable: PropTypes.objectOf(PropTypes.bool),
};

const PartnersContainer = connect(mapStateToProps, mapDispatchToProps)(PartnersContainerWrapper);

export default PartnersContainer;
